import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider,
  CircularProgress, Alert, FormControl, FormHelperText, InputLabel,
  Select, MenuItem, Autocomplete, ToggleButtonGroup,
  ToggleButton, InputAdornment, Switch, FormControlLabel,
} from '@mui/material';
import { Check, Search, PersonAdd, Payment, CardMembership, Warning } from '@mui/icons-material';
import MarkAsPaidCheckbox from './MarkAsPaidCheckbox';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useMyLocations } from '../hooks/useMyLocations';
import { studioIdOf } from '../utils/studioOf';
import BranchSelect from './BranchSelect';
import { stopWheelNudge } from '../utils/numberInput';
import SessionSeatPicker, { ChosenSeat } from './seat-layout/SessionSeatPicker';
import {
  formatPhoneInput,
  isValidPhoneNumber,
  validatePhoneNumber as validatePhoneField,
} from '../utils/phoneValidation';

interface UserInfo { _id: string; name?: string; email?: string; phoneNumber?: string; }
interface ClassType { _id: string; title: string; studioType?: 'workout' | 'wellness' | 'event'; }
interface SessionOption {
  _id: string; className?: string; date?: string; price?: number; duration?: number;
  /** Bikes free for a seated class, headcount otherwise — the server clamps it. */
  availableSlots?: number;
  sessionType?: string; isTrialEnabled?: boolean; trialPrice?: number;
  /**
   * True when this class runs in a room and a bike must be chosen. NEW field —
   * absent from a backend that predates the picker, which is why `seatLayout`
   * below is the fallback rather than an assumption of "seated".
   */
  hasSeatMap?: boolean;
  seatsFree?: number | null;
  seatLayoutName?: string | null;
  /**
   * The room id, straight off the session document. Present on EVERY backend
   * that has ever had seats (schema default null), so it is the reliable
   * answer to "does this class use bikes at all" during a deploy where the API
   * is older than this panel.
   */
  seatLayout?: string | null;
  /** Populated by the manual-booking endpoint; an id string if it ever isn't. */
  classType?: { _id: string; title?: string } | string | null;
}

const classTypeIdOf = (s: SessionOption) =>
  typeof s.classType === 'string' ? s.classType : s.classType?._id;
interface EventOption { _id: string; eventName: string; date?: string; time?: string; price?: number; slots?: number; availableSlots?: number; image?: string | null; }
interface Studio {
  _id: string;
  studioName: string;
  whitelabelConfig?: {
    appSource?: string;
    appName?: string;
  };
}
/**
 * One quota bucket of a combo pack. `class`/`category` arrive populated
 * ({_id, className|title}) but a raw id is still accepted — older responses and
 * any non-populating endpoint send one.
 */
interface PackAllowance {
  appliesTo: 'session' | 'class' | 'wellness';
  category?: { _id: string; title?: string } | string | null;
  class?: { _id: string; className?: string } | string | null;
  total?: number | null;
  remaining?: number | null;
}
interface ActivePack {
  _id: string; title?: string; pack?: { title?: string }; classesLeft?: number | null; isUnlimited?: boolean;
  expiryDate?: string; startDate?: string; categoryAllowances?: PackAllowance[];
  /** Shared pack: usable only for these categories / classes (populated or ids). */
  allowedCategories?: Array<{ _id: string; title?: string } | string>;
  allowedClasses?: Array<{ _id: string; className?: string } | string>;
}

const idOf = (v: any): string | null => (v == null ? null : typeof v === 'string' ? v : v._id ?? null);
/** A combo pack splits its total into per-class / per-class-type quotas. */
const isComboPack = (p?: ActivePack | null) => !!p?.categoryAllowances?.length;
/** Buckets a WALK-IN can be charged to: class-scoped or class-type-scoped, with room left. */
const spendableBuckets = (p?: ActivePack | null): PackAllowance[] =>
  (p?.categoryAllowances ?? []).filter(
    (b) => b.appliesTo !== 'wellness' && (b.remaining == null || b.remaining > 0),
  );
const bucketKey = (b: PackAllowance) => idOf(b.class) ?? idOf(b.category) ?? '*';
const bucketLabel = (b: PackAllowance) => {
  const cls = typeof b.class === 'object' && b.class ? b.class.className : null;
  const cat = typeof b.category === 'object' && b.category ? b.category.title : null;
  const name = cls || cat || (b.appliesTo === 'session' && !idOf(b.category) ? 'Any class' : 'Class');
  const left = b.remaining == null ? 'Unlimited' : `${b.remaining} left`;
  return `${name} — ${left}`;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  studios?: Studio[];
  prefilledUser?: UserInfo;
  prefilledEvent?: EventOption;
  prefilledStudio?: Studio;
  /**
   * Opened from a specific session's bookings list (bookings calendar view) —
   * skips the class-type/date/session pickers entirely and books straight
   * into this session, same as prefilledEvent does for events.
   */
  prefilledSession?: SessionOption;
  /** When opening from a purchased pack's history, prefer this pack for booking. */
  prefilledPackId?: string;
  /** That pack's startDate — when in the future, both booking modes' date pickers
   * are floored to it, since the pack can't be used any earlier. */
  prefilledPackStartDate?: string;
  /**
   * Show the Session|Event type toggle and let the admin pick an event from
   * the studio's own list, instead of requiring a prefilledEvent. Opt-in only
   * — other entry points (e.g. the generic "Add Manual Booking" on the
   * Bookings page) stay session-only, unchanged.
   */
  allowGenericEventBooking?: boolean;
}

const BOOKING_STATUSES = [
  { value: 'BOOKED', label: 'Booked' },
  { value: 'CHECKED_IN', label: 'Checked In' },
  { value: 'NO_SHOW', label: 'No Show' },
];

const SS_SOURCE = 'sweatsocial';

/**
 * Does this class run in a room?
 *
 * ── WHY TWO FIELDS ─────────────────────────────────────────────────────────
 * `hasSeatMap` is the answer the server computes, but it is NEW — a backend
 * that predates the bike picker does not send it. `seatLayout` is the raw
 * session field and has been on every response since seats existed (schema
 * default null), so it is the fallback.
 *
 * Reading only `hasSeatMap` would leave this `null` ("unknown") for every
 * studio during a deploy where the API is older than this panel — mounting the
 * picker, firing a seat-map request and flashing "Choose a bike" at 66 studios
 * that have never owned a bike. Chakra is the only studio with a room; every
 * other one must resolve to a hard `false` here, on any backend.
 *
 * `null` is reserved for a genuinely unknown session (none picked yet).
 */
const sessionRoomVerdict = (
  session: { hasSeatMap?: boolean; seatLayout?: string | null } | null,
): boolean | null => {
  if (!session) return null;
  if (typeof session.hasSeatMap === 'boolean') return session.hasSeatMap;
  return !!session.seatLayout;
};

const ManualBookingDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  studios = [],
  prefilledUser,
  prefilledEvent,
  prefilledStudio,
  prefilledSession,
  prefilledPackId,
  prefilledPackStartDate,
  allowGenericEventBooking = false,
}) => {
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin' || admin?.role === 'admin';

  // ── WHICH LOCATION IS THIS BOOKING AT ───────────────────────────────────
  // Under "All locations" a studio role has no implied location: the switcher
  // sends __all__, the server refuses the write, and every read below would
  // resolve to the sign-in branch. A dialog opened for a specific session /
  // event / pack takes that row's location; otherwise the operator picks one.
  const { isAllLocations } = useMyLocations();
  const [branchForBooking, setBranchForBooking] = useState('');
  const contextStudioId =
    studioIdOf((prefilledSession as any)?.studio) ||
    studioIdOf((prefilledEvent as any)?.studio) ||
    prefilledStudio?._id ||
    null;
  const mustPickBranch = !isSuperAdmin && isAllLocations && !contextStudioId;

  const [bookingType, setBookingType] = useState<'session' | 'event'>('session');

  // Studio
  const [selectedStudio, setSelectedStudio] = useState<Studio | null>(null);

  // Step 1: User
  const [phone, setPhone] = useState('');
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [userCheckResult, setUserCheckResult] = useState<{ exists: boolean; user?: UserInfo } | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [nameQuery, setNameQuery] = useState('');
  const [nameSearchResults, setNameSearchResults] = useState<UserInfo[]>([]);
  const [isSearchingByName, setIsSearchingByName] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserGender, setNewUserGender] = useState('');
  const [newUserAge, setNewUserAge] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState('');

  // Pack check (after user selected)
  const [isCheckingPacks, setIsCheckingPacks] = useState(false);
  const [userActivePacks, setUserActivePacks] = useState<ActivePack[]>([]);
  const [userUpcomingPacks, setUserUpcomingPacks] = useState<ActivePack[]>([]);
  const [bookingMode, setBookingMode] = useState<'quickCheckIn' | 'session'>('quickCheckIn');

  // Admin walk-in mode state
  const [walkInDate, setWalkInDate] = useState('');
  const [walkInTime, setWalkInTime] = useState('');
  const [walkInPack, setWalkInPack] = useState<ActivePack | null>(null);
  /**
   * Which class a combo-pack walk-in counts as. A walk-in has no class of its
   * own, so the pack cannot work out which quota to spend — the operator says.
   * Empty for ordinary packs, where the whole balance is interchangeable.
   */
  const [walkInPackClass, setWalkInPackClass] = useState<string>('');
  const [walkInPayment, setWalkInPayment] = useState<'subscription' | 'cash' | 'online' | 'free'>('cash');
  const [walkInPrice, setWalkInPrice] = useState('');
  const [walkInMarkAsPaid, setWalkInMarkAsPaid] = useState(true);
  const [walkInStatus, setWalkInStatus] = useState<'BOOKED' | 'CHECKED_IN' | 'NO_SHOW'>('CHECKED_IN');

  // Regular mode (no pack) — existing flow
  // Session category — 'workout' (default, unchanged behaviour) | 'wellness'.
  // Only surfaced for studios that offer wellness (see offersWellness).
  const [sessionCategory, setSessionCategory] = useState<'workout' | 'wellness'>('workout');
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [isLoadingClassTypes, setIsLoadingClassTypes] = useState(false);
  const [selectedClassType, setSelectedClassType] = useState<ClassType | null>(null);
  const [sessionDate, setSessionDate] = useState('');
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  // Every bookable session at this studio on `sessionDate` (null when no date
  // is picked, or while it loads). The Class Type picker is built from it so
  // it only offers types actually running that day.
  const [daySessions, setDaySessions] = useState<SessionOption[] | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionOption | null>(null);
  const [paymentType, setPaymentType] = useState<'cash' | 'online' | 'free' | 'pack'>('cash');
  // Record this session as the member's trial. Orthogonal to how it's paid
  // (cash / online / free are all valid) but mutually exclusive with a pack —
  // a trial IS the payment method, so charging it to a pack would spend a pack
  // class AND burn the trial. The backend rejects that pairing outright.
  const [bookAsTrial, setBookAsTrial] = useState(false);

  // Server-side rejections (trial already used, pack expired, slot gone…) render
  // at the BOTTOM of a long scrolling dialog, below the summary card. On a short
  // viewport the submit button sits over the fold, so the error appears
  // off-screen and the dialog looks like it silently did nothing. Scroll the
  // message into view whenever one appears.
  const errorRef = useRef<HTMLDivElement | null>(null);

  // An eligibility warning is a prompt, not a refusal: staff may knowingly grant
  // a goodwill trial. Holds the server's reason while we ask for confirmation.
  const [trialWarning, setTrialWarning] = useState<string>('');
  const [cashPrice, setCashPrice] = useState('');
  const [sessionMarkAsPaid, setSessionMarkAsPaid] = useState(false);
  const [sessionPacks, setSessionPacks] = useState<ActivePack[]>([]);
  const [isLoadingSessionPacks, setIsLoadingSessionPacks] = useState(false);
  const [selectedSessionPack, setSelectedSessionPack] = useState<ActivePack | null>(null);

  // ── The bike, for a class that runs in a room ────────────────────────────
  // `sessionSeat` is what gets sent. `sessionHasRoom` gates the Create button:
  // null = "not known yet", deliberately NOT the same as false — creating a
  // booking while unknown could put a member in a room with no machine.
  const [sessionSeat, setSessionSeat] = useState<ChosenSeat | null>(null);
  const [sessionHasRoom, setSessionHasRoom] = useState<boolean | null>(null);
  const [regularStatus, setRegularStatus] = useState<'BOOKED' | 'CHECKED_IN' | 'NO_SHOW'>('BOOKED');

  // Event mode state — either prefilled (opened from the Events page) or
  // picked here from the studio's own event list (generic entry points).
  const [selectedEvent, setSelectedEvent] = useState<EventOption | null>(null);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventPayment, setEventPayment] = useState<'cash' | 'online' | 'free'>('cash');
  const [eventMarkAsPaid, setEventMarkAsPaid] = useState(false);
  const [eventBookingError, setEventBookingError] = useState('');
  const [eventQuantity, setEventQuantity] = useState(1);
  const [eventAppSource, setEventAppSource] = useState<string>(SS_SOURCE);

  // Common
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Bring a newly-raised error into view. `block: 'center'` rather than
  // 'nearest' so the message lands mid-dialog instead of flush against the
  // action bar, where it reads as part of the buttons.
  useEffect(() => {
    if (!bookingError && !eventBookingError) return;
    // Wait for the Alert to mount before measuring it.
    const id = requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, [bookingError, eventBookingError]);

  const studioId = isSuperAdmin
    ? (selectedStudio?._id || prefilledStudio?._id)
    : isAllLocations
      ? (contextStudioId || branchForBooking || undefined)
      : (admin as any)?.studio?._id || admin?.id;
  // Sent as x-studio-id only under "All"; otherwise the switcher's own
  // single location goes out exactly as before.
  const pinStudioId = !isSuperAdmin && isAllLocations ? studioId : undefined;

  const studioForSource: Studio | null = isSuperAdmin
    ? (selectedStudio || prefilledStudio || null)
    : {
        _id: studioId || '',
        studioName: (admin as any)?.studio?.studioName || '',
        whitelabelConfig: (admin as any)?.studio?.whitelabelConfig,
      };

  // Does the selected/current studio offer wellness? Mirrors the gating used for
  // the Sessions "Workout | Wellness" toggle. Reads the raw studio doc, which
  // (for both super-admin `studios` and studio-admin `admin.studio`) carries
  // studioType / studioTypes / studioPermissions.enableWellness. Workout-only
  // studios evaluate to false, so the toggle never renders and the flow is unchanged.
  const wellnessStudio: any = isSuperAdmin
    ? (selectedStudio || prefilledStudio || null)
    : (admin as any)?.studio;
  const offersWellness =
    !!wellnessStudio?.studioPermissions?.enableWellness ||
    wellnessStudio?.studioType === 'wellness' ||
    (Array.isArray(wellnessStudio?.studioTypes) && wellnessStudio.studioTypes.includes('wellness'));

  const whitelabelAppSource = studioForSource?.whitelabelConfig?.appSource;
  const whitelabelAppName =
    studioForSource?.whitelabelConfig?.appName || studioForSource?.studioName || '';
  const hasWhitelabel = !!whitelabelAppSource && whitelabelAppSource !== SS_SOURCE;

  // ── Now: default today's date + current time ──
  const nowISO = () => {
    const n = new Date();
    const pad = (v: number) => String(v).padStart(2, '0');
    return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
  };
  const nowTime = () => {
    const n = new Date();
    const pad = (v: number) => String(v).padStart(2, '0');
    return `${pad(n.getHours())}:${pad(n.getMinutes())}`;
  };

  // Only set when the prefilled pack genuinely hasn't started yet — floors both
  // the Walk-In and Scheduled Class date pickers to it, since the pack can't be
  // used any earlier. minBookingDate is the ISO form the <input type="date"> min
  // attribute requires; minBookingDateDisplay is the human-readable form for the
  // helper text, so it doesn't read "2026-08-25" next to a "25-08-2026" field.
  const minBookingDate = (() => {
    if (!prefilledPackStartDate) return undefined;
    const start = new Date(prefilledPackStartDate);
    if (start <= new Date()) return undefined;
    const pad = (v: number) => String(v).padStart(2, '0');
    return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
  })();
  const minBookingDateDisplay = minBookingDate
    ? new Date(minBookingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
    : undefined;

  useEffect(() => {
    if (!open) { resetAll(); return; }
    if (prefilledUser) setSelectedUser(prefilledUser);
    if (prefilledEvent) {
      setBookingType('event');
      setSelectedEvent(prefilledEvent);
      if ((prefilledEvent.price ?? 0) <= 0) setEventPayment('free');
    }
    if (prefilledStudio) setSelectedStudio(prefilledStudio);
    if (prefilledSession) {
      setBookingType('session');
      setBookingMode('session');
      setSelectedSession(prefilledSession);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setEventAppSource(hasWhitelabel ? whitelabelAppSource! : SS_SOURCE);
  }, [selectedStudio, prefilledStudio, studioId, hasWhitelabel, whitelabelAppSource]);

  useEffect(() => {
    if (selectedUser && studioId && bookingType === 'session') {
      checkUserPacks();
      if (!prefilledSession) {
        // Default to today (or the pack's start day) so the Class Type list is
        // limited to what's actually running — the date effect below fetches it.
        if (sessionDate) fetchClassTypes(sessionCategory);
        else setSessionDate(minBookingDate || nowISO());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, studioId, bookingType]);

  // Picking or clearing a date rebuilds the Class Type list for that day.
  useEffect(() => {
    if (selectedUser && studioId && bookingType === 'session' && !prefilledSession) {
      fetchClassTypes(sessionCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionDate]);

  useEffect(() => {
    if (!selectedClassType || !studioId) return;
    if (sessionDate) {
      // The day's sessions are already loaded — just narrow them to the type.
      setSelectedSession(null);
      setSessions((daySessions || []).filter((s) => classTypeIdOf(s) === selectedClassType._id));
    } else {
      fetchSessions(selectedClassType._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassType, sessionDate, daySessions]);

  useEffect(() => {
    if (allowGenericEventBooking && bookingType === 'event' && !prefilledEvent && studioId) {
      fetchStudioEvents(studioId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowGenericEventBooking, bookingType, prefilledEvent, studioId]);

  useEffect(() => {
    if (paymentType === 'pack' && selectedUser && studioId && selectedSession) {
      fetchSessionPacks();
    } else {
      setSessionPacks([]);
      setSelectedSessionPack(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, selectedUser, studioId, selectedSession]);

  const handleNameSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setNameSearchResults([]); return; }
    setIsSearchingByName(true);
    try {
      if (isSuperAdmin) {
        const res = await apiService.searchUsers(q.trim());
        setNameSearchResults(res?.data || []);
      } else {
        const res = await apiService.getStudioMembers({ search: q.trim(), limit: 10 });
        setNameSearchResults((res?.data || []).map((c: any) => ({ _id: c._id, name: c.name, email: c.email, phoneNumber: c.phone || c.phoneNumber })));
      }
    } catch { setNameSearchResults([]); } finally { setIsSearchingByName(false); }
  }, [isSuperAdmin]);

  useEffect(() => {
    const t = setTimeout(() => { if (!selectedUser) handleNameSearch(nameQuery); }, 350);
    return () => clearTimeout(t);
  }, [nameQuery, handleNameSearch, selectedUser]);

  const resetAll = () => {
    setBookingType('session');
    setSelectedStudio(null); setBranchForBooking(''); setPhone(''); setIsCheckingUser(false);
    setUserCheckResult(null); setSelectedUser(null); setNameQuery('');
    setNameSearchResults([]); setIsSearchingByName(false);
    setNewUserName(''); setNewUserEmail(''); setNewUserGender(''); setNewUserAge('');
    setIsCreatingUser(false); setCreateUserError('');
    setIsCheckingPacks(false); setUserActivePacks([]);
    setBookingMode('quickCheckIn');
    setWalkInDate(''); setWalkInTime(''); setWalkInPack(null);
    setWalkInPayment('cash'); setWalkInPrice(''); setWalkInMarkAsPaid(true); setWalkInStatus('CHECKED_IN');
    setSessionCategory('workout');
    setClassTypes([]); setSelectedClassType(null); setSessionDate(''); setDaySessions(null);
    setSessions([]); setSelectedSession(null);
    setPaymentType('cash'); setCashPrice(''); setSessionMarkAsPaid(false); setBookAsTrial(false);
    setSessionPacks([]); setSelectedSessionPack(null);
    setRegularStatus('BOOKED');
    setSelectedEvent(null);
    setEventPayment('cash'); setEventMarkAsPaid(false);
    setEventBookingError(''); setEventQuantity(1);
    setEventAppSource(SS_SOURCE);
    setIsCreatingBooking(false); setBookingError(''); setBookingSuccess(false);
  };

  // Resets only the studio-dependent booking selections (packs, session/class
  // pickers, payment mode, picked event) — deliberately leaves selectedUser/
  // userCheckResult untouched so changing the studio never unselects the
  // already-picked user.
  const resetBookingSelections = () => {
    setUserActivePacks([]);
    setBookingMode(prefilledSession ? 'session' : 'quickCheckIn');
    setSessionCategory('workout');
    setSelectedClassType(null); setSessionDate(''); setDaySessions(null); setSessions([]);
    // A prefilledSession belongs to the fixed context this dialog was opened
    // for (mirrors prefilledEvent below) — don't clear it on a studio/user change.
    if (!prefilledSession) setSelectedSession(null);
    setPaymentType('cash'); setCashPrice(''); setSessionMarkAsPaid(false); setSessionPacks([]); setSelectedSessionPack(null); setBookAsTrial(false);
    setWalkInDate(''); setWalkInTime(''); setWalkInPack(null); setWalkInPayment('cash');
    setWalkInMarkAsPaid(true);
    setWalkInStatus('CHECKED_IN'); setRegularStatus('BOOKED');
    // Only clear a manually-picked event — a prefilledEvent belongs to the
    // fixed context this dialog was opened for and must survive a studio
    // change (which can't happen anyway while it's shown, see render guard).
    if (!prefilledEvent) {
      setSelectedEvent(null);
      setEvents([]);
    }
    setEventPayment('cash'); setEventMarkAsPaid(false); setEventBookingError(''); setEventQuantity(1);
  };

  const handleBookingTypeChange = (_: React.MouseEvent<HTMLElement>, val: 'session' | 'event' | null) => {
    if (!val || val === bookingType) return;
    setBookingType(val);
    resetBookingSelections();
  };

  const fetchStudioEvents = async (forStudioId: string) => {
    setIsLoadingEvents(true);
    try {
      const res = await apiService.getStudioEvents(forStudioId, undefined, pinStudioId);
      const list: any[] = res?.events || res?.data || [];
      // Same "naive IST < now IST" past check EventsPage uses — only offer
      // upcoming events for a new manual booking.
      const upcoming = list.filter((ev) => new Date(ev.date) >= new Date(Date.now() - 5.5 * 3600000));
      setEvents(
        upcoming.map((ev) => {
          const booked = ev.bookingCount ?? ev.interestedUsers?.length ?? 0;
          return {
            _id: ev._id,
            eventName: ev.eventName,
            date: ev.date,
            price: ev.price,
            slots: ev.slots,
            availableSlots: Math.max(0, (ev.slots ?? 0) - booked),
            image: ev.images?.[0] ?? null,
          } as EventOption;
        }),
      );
    } catch {
      setEvents([]);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const clearUser = () => {
    setSelectedUser(null); setUserCheckResult(null); setPhone(''); setNameQuery('');
    setNameSearchResults([]);
    resetBookingSelections();
  };

  const getFullPhone = () => `+91${phone.trim().replace(/^\+91/, '').replace(/\D/g, '')}`;

  const checkUserPacks = async () => {
    if (!studioId || !selectedUser) return;
    setIsCheckingPacks(true);
    try {
      // includeUpcoming so a not-yet-started pack can still be picked for Walk-In
      // once its start date is reached — see walkInAvailablePacks below, which
      // reveals it as soon as the Date field matches, no refetch needed.
      const res = await apiService.getUserActivePacksForBooking(selectedUser._id, studioId, undefined, true, pinStudioId);
      const packs: ActivePack[] = res.packs || [];
      setUserActivePacks(packs);
      setUserUpcomingPacks(res.upcomingPacks || []);
      const prefilledActivePack = prefilledPackId ? packs.find((p) => p._id === prefilledPackId) : undefined;
      const prefilledUpcomingPack = prefilledPackId && !prefilledActivePack
        ? (res.upcomingPacks || []).find((p: ActivePack) => p._id === prefilledPackId)
        : undefined;
      if (prefilledSession) {
        // Booking into a specific, already-known session — never auto-route
        // to Walk-In just because the member happens to have an active pack.
        setBookingMode('session');
      } else if (prefilledUpcomingPack) {
        // Quick Check-In books "now", so an upcoming pack can't use it yet.
        // Route to Session mode, pre-select this pack, and floor both date pickers
        // to its start date — never silently swap in a different pack.
        setBookingMode('session');
        setWalkInPack(prefilledUpcomingPack);
        setWalkInDate(minBookingDate || nowISO());
        setWalkInTime(nowTime());
        setWalkInStatus('CHECKED_IN');
        setWalkInPayment('subscription');
        setSessionDate(minBookingDate || nowISO());
      } else if (prefilledPackId && !prefilledActivePack) {
        // prefilledPackId matched neither an active nor an upcoming pack (e.g. expired)
        // — fall back to Session mode rather than Quick Check-In, which would
        // otherwise silently swap in a different, unrelated pack.
        setBookingMode('session');
      } else if (packs.length > 0) {
        setBookingMode('quickCheckIn');
        const preferred = prefilledActivePack || packs[0];
        setWalkInPack(preferred);
        setWalkInDate(nowISO());
        setWalkInTime(nowTime());
        setWalkInStatus('CHECKED_IN');
        setWalkInPayment('subscription');
      } else {
        setBookingMode('session');
      }
    } catch {
      setUserActivePacks([]);
      setUserUpcomingPacks([]);
    } finally {
      setIsCheckingPacks(false);
    }
  };

  const fetchClassTypes = async (category: 'workout' | 'wellness' = 'workout') => {
    if (sessionDate) {
      // A date is picked: offer only the class types with a bookable session
      // at this studio that day, taken from the day's sessions themselves.
      try {
        setIsLoadingClassTypes(true); setDaySessions(null);
        const res = await apiService.getSessionsForManualBooking(
          undefined,
          studioId,
          sessionDate,
          category === 'wellness' ? 'wellness' : undefined,
        );
        const list: SessionOption[] = res.sessions || [];
        const byId = new Map<string, ClassType>();
        list.forEach((s) => {
          if (s.classType && typeof s.classType === 'object') {
            byId.set(s.classType._id, { _id: s.classType._id, title: s.classType.title || 'Class' });
          }
        });
        const types = Array.from(byId.values()).sort((a, b) => a.title.localeCompare(b.title));
        setDaySessions(list);
        setClassTypes(types);
        setSelectedClassType((cur) => (cur && byId.has(cur._id) ? cur : null));
      } catch {
        setDaySessions([]); setClassTypes([]); setSelectedClassType(null);
      } finally { setIsLoadingClassTypes(false); }
      return;
    }
    setDaySessions(null);
    try {
      setIsLoadingClassTypes(true);
      // Workout (default) sends no filter → unchanged. Wellness narrows the
      // global class-type catalog to wellness types.
      const res = await apiService.getClassTypes(
        category === 'wellness' ? { studioType: 'wellness' } : undefined,
      );
      setClassTypes(res.classTypes || res || []);
    } catch { setClassTypes([]); } finally { setIsLoadingClassTypes(false); }
  };

  const handleSessionCategoryChange = (
    _: React.MouseEvent<HTMLElement>,
    val: 'workout' | 'wellness' | null,
  ) => {
    if (!val || val === sessionCategory) return;
    setSessionCategory(val);
    setSelectedClassType(null);
    setSelectedSession(null);
    setSessions([]);
    fetchClassTypes(val);
  };

  // No-date path only: upcoming sessions for one class type.
  const fetchSessions = async (classTypeId: string) => {
    try {
      setIsLoadingSessions(true); setSessions([]); setSelectedSession(null);
      const res = await apiService.getSessionsForManualBooking(
        classTypeId,
        studioId,
        undefined,
        sessionCategory === 'wellness' ? 'wellness' : undefined,
        pinStudioId,
      );
      setSessions(res.sessions || []);
    } catch { setSessions([]); } finally { setIsLoadingSessions(false); }
  };

  const fetchSessionPacks = async () => {
    if (!selectedUser || !studioId) return;
    try {
      setIsLoadingSessionPacks(true); setSessionPacks([]); setSelectedSessionPack(null);
      // includeUpcoming: a deferred-start pack is a valid payment option as long as
      // the chosen session falls on/after its startDate — the backend can't apply
      // that check itself since it isn't told which session is being booked, so it
      // returns upcoming packs separately and this filters them by selectedSession.
      const res = await apiService.getUserActivePacksForBooking(
        selectedUser._id, studioId, selectedSession?.sessionType, true, pinStudioId,
      );
      const usableUpcoming = (res.upcomingPacks || []).filter((p: ActivePack) => {
        if (!p.startDate || !selectedSession?.date) return false;
        return new Date(selectedSession.date) >= new Date(p.startDate);
      });
      // A shared pack scoped by CATEGORY only pays for its listed categories, so
      // don't offer it for any other class. (Class-scoped shared packs are left
      // to the server, which refuses with a clear message — sessions here carry
      // no class id.)
      const sessionCategory = selectedSession ? classTypeIdOf(selectedSession) : undefined;
      const coversSession = (p: ActivePack) => {
        const cats = (p.allowedCategories ?? []).map((c) => idOf(c)).filter(Boolean);
        return cats.length === 0 || !sessionCategory || cats.includes(sessionCategory);
      };
      setSessionPacks([...(res.packs || []), ...usableUpcoming].filter(coversSession));
    } catch { setSessionPacks([]); } finally { setIsLoadingSessionPacks(false); }
  };

  const handleCheckUser = async () => {
    const phoneError = validatePhoneField(phone);
    if (phoneError) return;
    try {
      setIsCheckingUser(true); setUserCheckResult(null); setSelectedUser(null);
      const result = await apiService.checkUserByPhone(getFullPhone(), pinStudioId);
      setUserCheckResult(result);
      if (result.exists && result.user) setSelectedUser(result.user);
    } catch { setUserCheckResult({ exists: false }); } finally { setIsCheckingUser(false); }
  };

  const handleCreateUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim()) { setCreateUserError('Name and email are required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUserEmail.trim())) {
      setCreateUserError('Enter a valid email address');
      return;
    }
    const ageNum = Number(newUserAge);
    if (!newUserAge.trim() || isNaN(ageNum)) { setCreateUserError('Age is required'); return; }
    if (ageNum < 18) { setCreateUserError('User must be at least 18 years old'); return; }
    try {
      setIsCreatingUser(true); setCreateUserError('');
      const result = await apiService.createUserForManualBooking({ phoneNumber: getFullPhone(), name: newUserName.trim(), email: newUserEmail.trim(), age: ageNum, gender: newUserGender || undefined }, pinStudioId);
      setSelectedUser(result.user || result);
    } catch (err: any) {
      setCreateUserError(err?.response?.data?.message || err.message || 'Failed to create user');
    } finally { setIsCreatingUser(false); }
  };

  // ── Admin Walk-In Booking ──────────────────────────────────────────────────
  const handleCreateAdminWalkIn = async () => {
    if (!selectedUser || !walkInDate || !walkInTime) return;
    if (walkInPayment === 'subscription' && !walkInPack) { setBookingError('Please select a pack'); return; }
    if (walkInPayment === 'subscription' && isComboPack(walkInPack) && !walkInPackClass) {
      setBookingError('This pack splits its classes by class type — choose which class this walk-in counts as.');
      return;
    }
    setIsCreatingBooking(true); setBookingError('');
    try {
      // Parse as local time (admin browser is IST) then convert to UTC
      const sessionDatetime = new Date(`${walkInDate}T${walkInTime}:00`).toISOString();
      await apiService.createAdminWalkIn({
        userId: selectedUser._id,
        sessionDatetime,
        bookingStatus: walkInStatus,
        modeOfPayment: walkInPayment,
        purchasedPackId: walkInPayment === 'subscription' ? walkInPack!._id : undefined,
        // Only meaningful for a combo pack; the backend ignores it otherwise.
        ...(walkInPayment === 'subscription' && isComboPack(walkInPack) && walkInPackClass
          ? { packClassId: walkInPackClass }
          : {}),
        basePrice: walkInPayment === 'cash' || walkInPayment === 'online'
          ? (parseFloat(walkInPrice) || 0)
          : 0,
        // ⚠️ isPaid must be sent for EVERY mode, not just online.
        //
        // Omitting it left the backend to default it to FALSE, so a free or
        // pack walk-in was stored as `isPaid: false` — an unpaid debt for a
        // class that cost nothing. The member was then blocked from booking
        // anything else ("You have a pending payment"), and the app's Pay
        // button 500'd because there was no amount to charge.
        //
        // Matches the session and event flows below, which have always sent
        // `paymentType === 'free' ? true : ...`.
        isPaid:
          walkInPayment === 'online'
            ? walkInMarkAsPaid
            // free, cash and pack are all settled at the moment of booking:
            // nothing is owed, the money is in the till, or a credit was spent.
            : true,
        studioId: isSuperAdmin ? studioId : undefined,
      }, pinStudioId);
      setBookingSuccess(true);
      setTimeout(() => { onSuccess(); onClose(); }, 1500);
    } catch (err: any) {
      setBookingError(err?.response?.data?.message || err.message || 'Failed to create booking');
    } finally { setIsCreatingBooking(false); }
  };

  // ── Event Booking ──────────────────────────────────────────────────────────
  const handleCreateEventBooking = async () => {
    if (!selectedUser || !selectedEvent) return;
    setIsCreatingBooking(true);
    setBookingError('');
    setEventBookingError('');
    try {
      const pricePerTicket = selectedEvent.price || 0;
      const isPaid = eventPayment === 'free' ? true : eventPayment === 'online' ? eventMarkAsPaid : true;
      await apiService.createManualBooking({
        userId: selectedUser._id,
        eventId: selectedEvent._id,
        isPaid,
        basePrice: eventPayment === 'free' ? 0 : pricePerTicket,
        taxes: 0,
        modeOfPayment: eventPayment === 'free' ? 'free' : eventPayment === 'online' ? 'online' : 'cash',
        studioId: isSuperAdmin ? studioId : undefined,
        quantity: eventQuantity,
        appSource: hasWhitelabel && isSuperAdmin ? eventAppSource : undefined,
      }, pinStudioId);
      setBookingSuccess(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Failed to create booking';
      if (msg.toLowerCase().includes('fully booked') || msg.toLowerCase().includes('not enough slots')) {
        setEventBookingError(msg);
      } else {
        setBookingError(msg);
      }
    } finally {
      setIsCreatingBooking(false);
    }
  };

  const handleBookAnotherEvent = () => {
    setSelectedUser(null);
    setUserCheckResult(null);
    setPhone('');
    setNameQuery('');
    setNameSearchResults([]);
    setEventBookingError('');
    setBookingError('');
    setBookingSuccess(false);
    if (prefilledEvent) {
      setSelectedEvent(prefilledEvent);
      setBookingType('event');
      setEventQuantity(1);
      setEventAppSource(hasWhitelabel ? whitelabelAppSource! : SS_SOURCE);
    }
  };

  // ── The bike follows the session ─────────────────────────────────────────
  // A different session is a different room, so any bike already chosen is
  // meaningless. Keyed on the session id rather than wired into each of the
  // five places that change `selectedSession` — a sixth added later inherits
  // this for free instead of silently carrying a foreign seatId.
  useEffect(() => {
    setSessionSeat(null);
    setSessionHasRoom(sessionRoomVerdict(selectedSession));
  }, [selectedSession]);

  // ── Regular Booking ────────────────────────────────────────────────────────
  const handleCreateRegularBooking = async (overrideTrialEligibility = false) => {
    if (!selectedUser || !selectedSession) return;
    if (paymentType === 'pack' && !selectedSessionPack) { setBookingError('Please select a pack'); return; }
    setIsCreatingBooking(true); setBookingError('');
    try {
      // A trial defaults to the session's trialPrice, not its normal price.
      const defaultPrice = bookAsTrial
        ? (selectedSession.trialPrice ?? 0)
        : (selectedSession.price || 0);
      const effectivePrice = paymentType === 'cash' || paymentType === 'online'
        ? (cashPrice !== '' ? parseFloat(cashPrice) : defaultPrice)
        : defaultPrice;
      const isPaid = paymentType === 'free' ? true : paymentType === 'online' ? sessionMarkAsPaid : true;
      await apiService.createManualBooking({
        userId: selectedUser._id,
        sessionId: selectedSession._id,
        isPaid,
        basePrice: paymentType === 'pack' ? 0 : effectivePrice,
        taxes: 0,
        modeOfPayment: paymentType === 'pack' ? 'subscription' : paymentType === 'free' ? 'free' : paymentType === 'online' ? 'online' : 'Cash',
        studioId: isSuperAdmin ? studioId : undefined,
        purchasedPackId: paymentType === 'pack' ? selectedSessionPack!._id : undefined,
        bookingStatus: regularStatus,
        // The bike the operator picked. Omitted for a class with no room, where
        // the server books by headcount exactly as it always has.
        ...(sessionSeat ? { seatId: sessionSeat.seatId } : {}),
        ...(bookAsTrial ? { isTrial: true } : {}),
        ...(overrideTrialEligibility ? { overrideTrialEligibility: true } : {}),
      }, pinStudioId);
      setBookingSuccess(true);
      setTrialWarning('');
      setTimeout(() => { onSuccess(); onClose(); }, 1500);
    } catch (err: any) {
      // apiService's interceptor flattens axios errors to { message, status,
      // code, overridable } — there is no `err.response` by the time we get
      // here. Falling back to err.response?.data only for any caller that
      // bypasses the interceptor.
      const overridable = err?.overridable === true || err?.response?.data?.overridable === true;
      const message = err?.message || err?.response?.data?.message || 'Failed to create booking';
      // A trial-eligibility warning is confirmable, not a dead end.
      if (overridable && !overrideTrialEligibility) {
        setTrialWarning(message);
      } else {
        setBookingError(message);
      }
    } finally { setIsCreatingBooking(false); }
  };

  const formatDate = (d?: string) => {
    if (!d) return 'N/A';
    try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }); }
    catch { return d; }
  };

  const getUserInitials = (name?: string) => {
    if (!name) return 'U';
    const p = name.trim().split(' ').filter(Boolean);
    return p.length > 1 ? `${p[0][0]}${p[p.length - 1][0]}`.toUpperCase() : p[0]?.[0]?.toUpperCase() || 'U';
  };

  const packLabel = (p: ActivePack) => {
    const title = p.pack?.title || p.title || 'Pack';
    const classes = p.isUnlimited || p.classesLeft === null || p.classesLeft === -1 ? 'Unlimited' : `${p.classesLeft} left`;
    const expiry = p.expiryDate ? ` · Exp ${new Date(p.expiryDate).toLocaleDateString('en-IN')}` : '';
    const starts = p.startDate && new Date(p.startDate) > new Date()
      ? ` · Starts ${new Date(p.startDate).toLocaleDateString('en-IN')}`
      : '';
    return `${title} (${classes}${expiry}${starts})`;
  };

  const walkInPaymentSummaryRow = (): { label: string; value: string } => {
    if (walkInPayment === 'subscription') {
      return { label: 'Payment', value: walkInPack ? 'Paid via pack' : '—' };
    }
    if (walkInPayment === 'free') return { label: 'Payment', value: 'Free' };
    if (walkInPayment === 'online') {
      return {
        label: 'Payment',
        value: `Online · ₹${walkInPrice || 0} (${walkInMarkAsPaid ? 'Paid' : 'Pending'})`,
      };
    }
    return { label: 'Payment', value: `Cash · ₹${walkInPrice || 0}` };
  };

  const sessionPaymentSummaryRow = (): { label: string; value: string } => {
    // Trial defaults to trialPrice, so the summary must too or it shows the
    // normal session price for a booking that won't be charged that.
    const fallback = bookAsTrial
      ? (selectedSession?.trialPrice ?? 0)
      : (selectedSession?.price || 0);
    const amount = cashPrice !== '' ? parseFloat(cashPrice) : fallback;
    const formatted = amount.toFixed(2);
    const trialTag = bookAsTrial ? 'Trial · ' : '';
    if (paymentType === 'pack') {
      return { label: 'Payment', value: selectedSessionPack ? 'Paid via pack' : '—' };
    }
    if (paymentType === 'free') return { label: 'Payment', value: `${trialTag}Free` };
    if (paymentType === 'online') {
      return {
        label: 'Payment',
        value: `${trialTag}Online · ₹${formatted} (${sessionMarkAsPaid ? 'Paid' : 'Pending'})`,
      };
    }
    return { label: 'Payment', value: `${trialTag}Cash · ₹${formatted}` };
  };

  // Packs selectable for Walk-In right now: active ones, plus any upcoming pack
  // whose startDate the chosen walkInDate has reached — recomputed on every
  // render, so ticking the Date field forward reveals a pack the moment it
  // qualifies, with no refetch.
  const walkInAvailablePacks: ActivePack[] = [
    ...userActivePacks,
    ...userUpcomingPacks.filter((p) => walkInDate && p.startDate && new Date(walkInDate) >= new Date(p.startDate)),
  ];

  const handleBookingModeChange = (_: React.MouseEvent<HTMLElement>, val: 'quickCheckIn' | 'session' | null) => {
    if (!val) return;
    setBookingMode(val);
    if (val === 'quickCheckIn') {
      if (!walkInDate) setWalkInDate(nowISO());
      if (!walkInTime) setWalkInTime(nowTime());
      if (walkInAvailablePacks.length === 0 && walkInPayment === 'subscription') {
        setWalkInPayment('cash');
      }
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2, fontWeight: 700 }}>
        Add Manual Booking
      </DialogTitle>

      <DialogContent sx={{ pt: '24px !important' }}>
        {bookingSuccess ? (
          <Box textAlign="center" py={3}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: 'success.light', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Check sx={{ color: 'white', fontSize: 36 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="success.main">Booking Created!</Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>The booking has been successfully created.</Typography>
            {bookingType === 'event' && (
              <Box display="flex" gap={1} justifyContent="center" mt={2}>
                {prefilledEvent && (
                  <Button variant="outlined" onClick={handleBookAnotherEvent}>Book Another</Button>
                )}
                <Button variant="contained" onClick={() => { onSuccess(); onClose(); }}>Done</Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            {/* Super Admin: Studio */}
            {isSuperAdmin && !prefilledEvent && !prefilledSession && !(prefilledStudio && prefilledPackId) && (
              <Box mb={3}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Studio</Typography>
                <Autocomplete
                  options={studios}
                  getOptionLabel={(s) => s.studioName}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  value={selectedStudio}
                  onChange={(_, val) => { setSelectedStudio(val); resetBookingSelections(); }}
                  renderInput={(params) => <TextField {...params} label="Select Studio" size="small" required />}
                />
              </Box>
            )}

            {isSuperAdmin && prefilledStudio && (prefilledEvent || prefilledPackId || prefilledSession) && (
              <Box mb={3} p={1.5} sx={{ bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Studio</Typography>
                <Typography variant="body2" fontWeight={600}>{prefilledStudio.studioName}</Typography>
              </Box>
            )}

            {prefilledEvent && (
              <Box mb={3} p={1.5} sx={{ bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">Event</Typography>
                <Typography variant="body2" fontWeight={600}>{prefilledEvent.eventName}</Typography>
                {prefilledEvent.date && (
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(prefilledEvent.date)}
                    {prefilledEvent.availableSlots != null ? ` · ${prefilledEvent.availableSlots} slots left` : ''}
                  </Typography>
                )}
              </Box>
            )}

            {mustPickBranch && (
              <Box mb={3}>
                <BranchSelect
                  value={branchForBooking}
                  onChange={(v) => { setBranchForBooking(v); resetBookingSelections(); }}
                  minRole="manager"
                />
              </Box>
            )}

            {isSuperAdmin && !selectedStudio && !prefilledStudio ? (
              <Alert severity="info">Please select a studio to continue.</Alert>
            ) : mustPickBranch && !branchForBooking ? (
              <Alert severity="info">Choose which location this booking is for.</Alert>
            ) : (
              <>
                {/* Booking type — opt-in (allowGenericEventBooking) and hidden
                    once the flow is already fixed to a specific event
                    (prefilledEvent) or a specific pack (prefilledPackId
                    implies a session booked against it). */}
                {allowGenericEventBooking && !prefilledEvent && !prefilledPackId && (
                  <Box mb={3}>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>Booking Type</Typography>
                    <ToggleButtonGroup
                      value={bookingType}
                      exclusive
                      onChange={handleBookingTypeChange}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton value="session">Session</ToggleButton>
                      <ToggleButton value="event">Event</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}

                {/* ── Step 1: Find / Create User (hidden when prefilledUser provided) ── */}
                {!prefilledUser && (
                  <>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>Step 1: Find or Create User</Typography>

                    <Box display="flex" gap={1} mb={1}>
                      <TextField
                        label="Phone Number" placeholder="9876543210"
                        value={phone}
                        onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                        size="small" fullWidth inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                        InputProps={{ startAdornment: <Box component="span" sx={{ mr: 0.5, color: 'text.secondary', fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>+91</Box> }}
                        helperText={phone.trim() && !isValidPhoneNumber(phone) ? 'Enter a valid 10-digit phone number' : undefined}
                        error={Boolean(phone.trim()) && !isValidPhoneNumber(phone)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCheckUser()}
                        disabled={!!selectedUser}
                      />
                      {!selectedUser && (
                        <Button variant="contained" onClick={handleCheckUser}
                          disabled={isCheckingUser || !isValidPhoneNumber(phone)}
                          startIcon={isCheckingUser ? <CircularProgress size={16} color="inherit" /> : <Search />}
                          sx={{ whiteSpace: 'nowrap' }}>
                          Search
                        </Button>
                      )}
                      {selectedUser && <Button variant="outlined" onClick={clearUser}>Clear</Button>}
                    </Box>

                    {!selectedUser && (
                      <>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Divider sx={{ flex: 1 }} />
                          <Typography variant="caption" color="text.secondary">or search by name</Typography>
                          <Divider sx={{ flex: 1 }} />
                        </Box>
                        <Autocomplete
                          options={nameSearchResults}
                          getOptionLabel={(u) => `${u.name || ''} ${u.phoneNumber ? `(${u.phoneNumber})` : ''} ${u.email ? `· ${u.email}` : ''}`.trim()}
                          inputValue={nameQuery}
                          onInputChange={(_, v) => { if (!selectedUser) setNameQuery(v); }}
                          onChange={(_, val) => { if (val) setSelectedUser(val); }}
                          loading={isSearchingByName}
                          renderInput={(params) => (
                            <TextField {...params} label="Search by name" size="small" fullWidth
                              InputProps={{ ...params.InputProps, endAdornment: (<>{isSearchingByName ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                            />
                          )}
                          sx={{ mb: 1 }}
                        />
                      </>
                    )}

                    {/* User found / create form */}
                    {userCheckResult && !selectedUser && (
                      <Box mt={1} mb={2} p={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        {userCheckResult.exists ? null : (
                          <>
                            <Alert severity="warning" sx={{ mb: 2 }}>No user found. Create a new account:</Alert>
                            <TextField label="Name" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} size="small" fullWidth sx={{ mb: 1 }} required />
                            <TextField label="Email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} size="small" fullWidth sx={{ mb: 1 }} required />
                            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                              <InputLabel>Gender</InputLabel>
                              <Select value={newUserGender} label="Gender" onChange={(e) => setNewUserGender(e.target.value)}>
                                <MenuItem value="">Prefer not to say</MenuItem>
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField label="Age" type="number" value={newUserAge} onChange={(e) => setNewUserAge(e.target.value)} onWheel={stopWheelNudge} size="small" fullWidth sx={{ mb: 1.5 }} required inputProps={{ min: 18 }} helperText="Must be 18 or older" />
                            {createUserError && <Alert severity="error" sx={{ mb: 1 }}>{createUserError}</Alert>}
                            <Button variant="contained" fullWidth startIcon={isCreatingUser ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
                              onClick={handleCreateUser} disabled={isCreatingUser}>
                              Create User
                            </Button>
                          </>
                        )}
                      </Box>
                    )}
                  </>
                )}

                {/* Selected user badge */}
                {selectedUser && (
                  <Box display="flex" alignItems="center" gap={1.5} mb={2} p={1.5}
                    sx={{ backgroundColor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="white" fontWeight={700}>{getUserInitials(selectedUser.name)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{selectedUser.name || 'Unknown'}</Typography>
                      <Typography variant="caption" color="text.secondary">{selectedUser.phoneNumber} {selectedUser.email ? `· ${selectedUser.email}` : ''}</Typography>
                    </Box>
                    {isCheckingPacks && <CircularProgress size={16} sx={{ ml: 'auto' }} />}
                  </Box>
                )}

                {/* ── Step 2: Select Event (event flow, when not opened with a fixed prefilledEvent) ── */}
                {allowGenericEventBooking && bookingType === 'event' && !prefilledEvent && selectedUser && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={600} mb={2}>Step 2: Select Event</Typography>
                    <Autocomplete
                      options={events}
                      getOptionLabel={(e) => `${e.eventName}${e.date ? ` — ${formatDate(e.date)}` : ''}`}
                      isOptionEqualToValue={(a, b) => a._id === b._id}
                      value={selectedEvent}
                      loading={isLoadingEvents}
                      onChange={(_, val) => {
                        setSelectedEvent(val);
                        setEventQuantity(1);
                        setEventPayment(val && (val.price ?? 0) <= 0 ? 'free' : 'cash');
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Event" size="small" required
                          InputProps={{ ...params.InputProps, endAdornment: (<>{isLoadingEvents ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                        />
                      )}
                    />
                    {!isLoadingEvents && events.length === 0 && (
                      <Alert severity="info" sx={{ mt: 1 }}>No upcoming events for this studio.</Alert>
                    )}
                  </>
                )}

                {/* Session booking mode toggle — hidden when opened for a specific
                    prefilled session, since Walk-In would abandon that session. */}
                {bookingType === 'session' && selectedUser && !isCheckingPacks && !prefilledSession && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary" mb={0.5} display="block">Booking Type</Typography>
                    <ToggleButtonGroup
                      value={bookingMode}
                      exclusive
                      onChange={handleBookingModeChange}
                      size="small"
                      fullWidth
                      sx={{ mb: 0.75 }}
                    >
                      <ToggleButton value="quickCheckIn">Walk-In</ToggleButton>
                      <ToggleButton value="session">Scheduled Class</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {bookingMode === 'quickCheckIn'
                        ? 'Member is here now — check them in without picking a class slot.'
                        : 'Book them into a class already on the schedule.'}
                    </Typography>
                  </>
                )}

                {/* ── Quick Check-In Mode — session only ── */}
                {bookingType === 'session' && selectedUser && bookingMode === 'quickCheckIn' && !isCheckingPacks && !prefilledSession && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box mb={2} />

                    {/* Date + Time */}
                    <Box display="flex" gap={2} mb={2}>
                      <TextField label="Date" type="date" size="small" fullWidth value={walkInDate}
                        onChange={(e) => setWalkInDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={minBookingDate ? { min: minBookingDate } : undefined}
                        helperText={minBookingDateDisplay ? `Pack starts ${minBookingDateDisplay}` : undefined}
                        required />
                      <TextField label="Time" type="time" size="small" fullWidth value={walkInTime}
                        onChange={(e) => setWalkInTime(e.target.value)}
                        InputLabelProps={{ shrink: true }} required />
                    </Box>

                    {/* Booking Status */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Booking Status</InputLabel>
                      <Select value={walkInStatus} label="Booking Status"
                        onChange={(e) => setWalkInStatus(e.target.value as any)}>
                        {BOOKING_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                      </Select>
                    </FormControl>

                    {/* Payment Mode */}
                    <Typography variant="caption" color="text.secondary" mb={0.5} display="block">Payment Method</Typography>
                    <ToggleButtonGroup value={walkInPayment} exclusive
                      onChange={(_, val) => { if (val) setWalkInPayment(val); }}
                      size="small" sx={{ mb: 2, flexWrap: 'wrap' }}>
                      {walkInAvailablePacks.length > 0 && (
                        <ToggleButton value="subscription"><CardMembership sx={{ fontSize: 16, mr: 0.75 }} />Use Pack</ToggleButton>
                      )}
                      <ToggleButton value="cash"><Payment sx={{ fontSize: 16, mr: 0.75 }} />Cash</ToggleButton>
                      <ToggleButton value="online"><Payment sx={{ fontSize: 16, mr: 0.75 }} />Online</ToggleButton>
                      <ToggleButton value="free"><Check sx={{ fontSize: 16, mr: 0.75 }} />Free</ToggleButton>
                    </ToggleButtonGroup>

                    {/* Pack picker */}
                    {walkInPayment === 'subscription' && (
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Select Pack</InputLabel>
                        <Select value={walkInPack?._id || ''} label="Select Pack"
                          onChange={(e) => {
                            const p = walkInAvailablePacks.find(p => p._id === e.target.value);
                            setWalkInPack(p || null);
                            // A bucket chosen for the previous pack means nothing
                            // for this one — never carry it across.
                            const buckets = spendableBuckets(p);
                            setWalkInPackClass(buckets.length === 1 ? bucketKey(buckets[0]) : '');
                          }}>
                          {walkInAvailablePacks.map((p) => <MenuItem key={p._id} value={p._id}>{packLabel(p)}</MenuItem>)}
                        </Select>
                      </FormControl>
                    )}

                    {/*
                      A walk-in creates a synthetic "Manual Check-In" session with no
                      class of its own. An ordinary pack does not care — any class in
                      the balance is interchangeable. A COMBO pack's balance is split
                      per class, so it cannot know which quota to spend: every such
                      walk-in used to be rejected with "No classes remaining in the
                      selected pack for this category" on a pack that plainly had
                      classes left. The operator decides here.
                    */}
                    {walkInPayment === 'subscription' && isComboPack(walkInPack) && (
                      <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!walkInPackClass}>
                        <InputLabel>Counts as</InputLabel>
                        <Select value={walkInPackClass} label="Counts as"
                          onChange={(e) => setWalkInPackClass(e.target.value)}>
                          {spendableBuckets(walkInPack).map((b) => (
                            <MenuItem key={bucketKey(b)} value={bucketKey(b)}>{bucketLabel(b)}</MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {spendableBuckets(walkInPack).length === 0
                            ? 'Every quota in this pack is used up — it cannot pay for this walk-in.'
                            : 'This pack splits its classes by class type. Pick which quota to deduct.'}
                        </FormHelperText>
                      </FormControl>
                    )}

                    {/* Cash / online amount */}
                    {(walkInPayment === 'cash' || walkInPayment === 'online') && (
                      <TextField label="Amount (₹)" size="small" type="number" fullWidth
                        value={walkInPrice} onChange={(e) => setWalkInPrice(e.target.value)}
                        onWheel={stopWheelNudge}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                        sx={{ mb: 2 }} />
                    )}

                    {walkInPayment === 'online' && (
                      <MarkAsPaidCheckbox
                        checked={walkInMarkAsPaid}
                        onChange={setWalkInMarkAsPaid}
                      />
                    )}

                    {/* Summary */}
                    <Box p={2} sx={{ backgroundColor: 'grey.50', borderRadius: 2, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Booking Summary</Typography>
                      {[
                        { label: 'User', value: selectedUser.name || 'N/A' },
                        { label: 'Session', value: 'Manual Check-In (By Admin)' },
                        { label: 'Date & Time', value: walkInDate && walkInTime ? `${walkInDate} at ${walkInTime}` : '—' },
                        { label: 'Status', value: BOOKING_STATUSES.find(s => s.value === walkInStatus)?.label || walkInStatus },
                        walkInPaymentSummaryRow(),
                      ].map(({ label, value }) => (
                        <Box key={label} display="flex" justifyContent="space-between" mb={0.75}>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" fontWeight={600}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {walkInPayment === 'subscription' && walkInPack && (
                      <Alert severity="info" icon={<CardMembership />} sx={{ mb: 1 }}>
                        1 class will be deducted from <strong>{packLabel(walkInPack)}</strong>
                        {/*
                          Naming the bucket matters: this notice used to promise a
                          deduction the backend then refused, so the operator saw
                          "1 class will be deducted" and an error side by side.
                        */}
                        {isComboPack(walkInPack) && (() => {
                          const b = spendableBuckets(walkInPack).find((x) => bucketKey(x) === walkInPackClass);
                          return b ? <> — from its <strong>{bucketLabel(b)}</strong> quota</> : null;
                        })()}
                        .
                      </Alert>
                    )}

                    {bookingError && <Alert ref={errorRef} severity="error" sx={{ mt: 1 }}>{bookingError}</Alert>}
                  </>
                )}

                {/* ── Book a Session Mode ── */}
                {bookingType === 'session' && selectedUser && bookingMode === 'session' && !isCheckingPacks && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={600} mb={2}>Step 2: Select Session</Typography>

                    {prefilledSession ? (
                      <Box mb={2} p={1.5} sx={{ bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">Session</Typography>
                        <Typography variant="body2" fontWeight={600}>{prefilledSession.className}</Typography>
                        {prefilledSession.date && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatDate(prefilledSession.date)}
                            {prefilledSession.availableSlots != null ? ` · ${prefilledSession.availableSlots} slots left` : ''}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <>
                        {/* Workout | Wellness — only for studios that offer wellness.
                            Workout-only studios never see this; their flow is unchanged. */}
                        {offersWellness && (
                          <ToggleButtonGroup
                            value={sessionCategory}
                            exclusive
                            onChange={handleSessionCategoryChange}
                            size="small"
                            fullWidth
                            sx={{ mb: 2 }}
                          >
                            <ToggleButton value="workout">Workout</ToggleButton>
                            <ToggleButton value="wellness">Wellness</ToggleButton>
                          </ToggleButtonGroup>
                        )}

                        <TextField label="Date" type="date" size="small" fullWidth
                          value={sessionDate} onChange={(e) => { setSessionDate(e.target.value); setDaySessions(null); setSessions([]); setSelectedSession(null); }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={minBookingDate ? { min: minBookingDate } : undefined}
                          helperText={minBookingDateDisplay ? `Pack starts ${minBookingDateDisplay} — pick that date or later` : 'Pick any date — past or upcoming'}
                          sx={{ mb: 2 }} />

                        {isLoadingClassTypes ? <CircularProgress size={24} /> : sessionDate && classTypes.length === 0 ? (
                          <Alert severity="info">No classes with open spots on {sessionDate}.</Alert>
                        ) : (
                          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Class Type</InputLabel>
                            <Select value={selectedClassType?._id || ''} label="Class Type"
                              onChange={(e) => { const ct = classTypes.find(c => c._id === e.target.value); setSelectedClassType(ct || null); }}>
                              {classTypes.map((ct) => <MenuItem key={ct._id} value={ct._id}>{ct.title}</MenuItem>)}
                            </Select>
                          </FormControl>
                        )}

                        {selectedClassType && (
                          isLoadingSessions ? <CircularProgress size={24} /> :
                          sessions.length === 0 ? (
                            <Alert severity="info">{sessionDate ? `No sessions found for ${sessionDate}.` : 'No upcoming sessions for this class type.'}</Alert>
                          ) : (
                            <FormControl fullWidth size="small">
                              <InputLabel>Session</InputLabel>
                              <Select value={selectedSession?._id || ''} label="Session"
                                onChange={(e) => { const s = sessions.find(s => s._id === e.target.value); setSelectedSession(s || null); setCashPrice(''); setBookAsTrial(false); }}>
                                {sessions.map((s) => <MenuItem key={s._id} value={s._id}>{s.className} — {formatDate(s.date)} ({s.availableSlots} slots)</MenuItem>)}
                              </Select>
                            </FormControl>
                          )
                        )}
                      </>
                    )}

                    {selectedSession && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight={600} mb={2}>Step 3: Payment & Status</Typography>

                        {/* Booking Status */}
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Booking Status</InputLabel>
                          <Select value={regularStatus} label="Booking Status"
                            onChange={(e) => setRegularStatus(e.target.value as any)}>
                            {BOOKING_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                          </Select>
                        </FormControl>

                        {/* Trial toggle — only when this session is actually
                            offered as a trial. Hidden entirely otherwise, so
                            studios that don't run trials never see it. */}
                        {selectedSession?.isTrialEnabled && (
                          <Box
                            mb={2}
                            p={1.5}
                            sx={{
                              border: '1px solid',
                              borderColor: bookAsTrial ? 'primary.main' : 'divider',
                              borderRadius: 2,
                              bgcolor: bookAsTrial ? 'primary.50' : 'transparent',
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={bookAsTrial}
                                  onChange={(e) => {
                                    const on = e.target.checked;
                                    setBookAsTrial(on);
                                    // A trial can never be paid with a pack —
                                    // fall back to cash rather than submitting a
                                    // combination the server will reject.
                                    if (on && paymentType === 'pack') setPaymentType('cash');
                                    if (on) setCashPrice(String(selectedSession?.trialPrice ?? 0));
                                    else setCashPrice('');
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    Book as trial class
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {/* "per studio" only means something to a
                                        super admin who works across studios. A
                                        studio owner or manager sees one studio,
                                        so it reads as noise. */}
                                    Trial price ₹{selectedSession?.trialPrice ?? 0}
                                    {isSuperAdmin
                                      ? ' · one trial per member, per studio'
                                      : ' · one trial per member'}
                                  </Typography>
                                </Box>
                              }
                            />
                          </Box>
                        )}

                        <Typography variant="caption" color="text.secondary" mb={0.5} display="block">Payment Method</Typography>
                        <ToggleButtonGroup value={paymentType} exclusive
                          onChange={(_, val) => { if (val) setPaymentType(val); }}
                          size="small" sx={{ mb: 2, flexWrap: 'wrap' }}>
                          <ToggleButton value="cash"><Payment sx={{ fontSize: 16, mr: 0.75 }} />Cash</ToggleButton>
                          <ToggleButton value="online"><Payment sx={{ fontSize: 16, mr: 0.75 }} />Online</ToggleButton>
                          <ToggleButton value="free"><Check sx={{ fontSize: 16, mr: 0.75 }} />Free</ToggleButton>
                          {/* Disabled, not hidden: the operator should see WHY a
                              pack can't be used rather than wonder where it went. */}
                          <ToggleButton value="pack" disabled={bookAsTrial}>
                            <CardMembership sx={{ fontSize: 16, mr: 0.75 }} />Use Pack
                          </ToggleButton>
                        </ToggleButtonGroup>
                        {bookAsTrial && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1.5, mb: 2 }}>
                            A trial can't be paid with a pack — charge it as cash, online or free.
                          </Typography>
                        )}

                        {(paymentType === 'cash' || paymentType === 'online') && (
                          <TextField label="Amount" size="small" type="number" fullWidth
                            value={cashPrice !== '' ? cashPrice : (selectedSession?.price ?? '')}
                            onChange={(e) => setCashPrice(e.target.value)}
                            onWheel={stopWheelNudge}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            helperText={
                              bookAsTrial
                                ? `Trial price: ₹${selectedSession?.trialPrice ?? 0}`
                                : `Default: ₹${selectedSession?.price ?? 0}`
                            } sx={{ mb: 2 }} />
                        )}

                        {paymentType === 'online' && (
                          <MarkAsPaidCheckbox
                            checked={sessionMarkAsPaid}
                            onChange={setSessionMarkAsPaid}
                          />
                        )}

                        {paymentType === 'pack' && (
                          <Box mb={2}>
                            {isLoadingSessionPacks ? (
                              <Box display="flex" alignItems="center" gap={1}><CircularProgress size={20} /><Typography variant="body2" color="text.secondary">Loading packs…</Typography></Box>
                            ) : sessionPacks.length === 0 ? (
                              <Alert severity="warning">No active packs for this user at this studio.</Alert>
                            ) : (
                              <FormControl fullWidth size="small">
                                <InputLabel>Select Pack</InputLabel>
                                <Select value={selectedSessionPack?._id || ''} label="Select Pack"
                                  onChange={(e) => { const p = sessionPacks.find(p => p._id === e.target.value); setSelectedSessionPack(p || null); }}>
                                  {sessionPacks.map((p) => <MenuItem key={p._id} value={p._id}>{packLabel(p)}</MenuItem>)}
                                </Select>
                              </FormControl>
                            )}
                          </Box>
                        )}

                        {/* The room, for a class that runs in one.
                            `sessionHasRoom !== false` — not just "a session is
                            picked" — so a class the list has ALREADY told us has
                            no room never mounts this: no wasted request, and no
                            way for a network blip on the seat-map call to strand
                            the Create button behind a bike that cannot be chosen.
                            `null` (an older backend that omits the field) still
                            mounts, and the picker resolves it. */}
                        {sessionHasRoom !== false && (
                          <Box mb={2}>
                            <SessionSeatPicker
                              sessionId={selectedSession._id}
                              value={sessionSeat}
                              onChange={setSessionSeat}
                              onRoomResolved={setSessionHasRoom}
                            />
                          </Box>
                        )}

                        {/* Summary */}
                        <Box p={2} sx={{ backgroundColor: 'grey.50', borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Booking Summary</Typography>
                          {[
                            { label: 'User', value: selectedUser?.name || 'N/A' },
                            { label: 'Class', value: selectedSession.className || 'N/A' },
                            { label: 'Date', value: formatDate(selectedSession.date) },
                            { label: 'Duration', value: `${selectedSession.duration || 0} min` },
                            { label: 'Status', value: BOOKING_STATUSES.find(s => s.value === regularStatus)?.label || regularStatus },
                            // Only when the class actually has a room — a blank
                            // "Bike —" row on every other studio's summary is noise.
                            ...(sessionHasRoom
                              ? [{
                                  label: 'Bike',
                                  value: sessionSeat
                                    ? `${sessionSeat.number ?? sessionSeat.seatId} · row ${sessionSeat.row}`
                                    : 'Not chosen',
                                }]
                              : []),
                            sessionPaymentSummaryRow(),
                          ].map(({ label, value }) => (
                            <Box key={label} display="flex" justifyContent="space-between" mb={0.75}>
                              <Typography variant="body2" color="text.secondary">{label}</Typography>
                              <Typography variant="body2" fontWeight={600}>{value}</Typography>
                            </Box>
                          ))}
                        </Box>

                        {paymentType === 'pack' && selectedSessionPack && (
                          <Alert severity="info" icon={<CardMembership />} sx={{ mt: 1.5 }}>
                            1 class will be deducted from <strong>{packLabel(selectedSessionPack)}</strong>.
                          </Alert>
                        )}

                        {bookingError && <Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>{bookingError}</Alert>}
                      </>
                    )}
                  </>
                )}

                {bookingType === 'event' && selectedUser && selectedEvent && hasWhitelabel && isSuperAdmin && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Booking Source</Typography>
                      <ToggleButtonGroup
                        value={eventAppSource}
                        exclusive
                        onChange={(_, val) => val && setEventAppSource(val)}
                        fullWidth
                        size="small"
                      >
                        <ToggleButton value={whitelabelAppSource!} sx={{ flexDirection: 'column', py: 1.5 }}>
                          <Typography variant="body2" fontWeight={700}>{whitelabelAppName.toUpperCase()}</Typography>
                          <Typography variant="caption" color="text.secondary">Whitelabel app</Typography>
                        </ToggleButton>
                        <ToggleButton value={SS_SOURCE} sx={{ flexDirection: 'column', py: 1.5 }}>
                          <Typography variant="body2" fontWeight={700}>Sweat Social</Typography>
                          <Typography variant="caption" color="text.secondary">SweatSocial platform</Typography>
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Box mt={1} p={1} bgcolor="action.hover" borderRadius={1}>
                        <Typography variant="caption" color="text.secondary">
                          {eventAppSource === SS_SOURCE
                            ? 'Booking will be attributed to SweatSocial platform.'
                            : `Booking will be attributed to ${whitelabelAppName}.`}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {bookingType === 'event' && selectedUser && selectedEvent && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={600} mb={2}>
                      {(() => {
                        // An extra "Select Event" step is inserted before this
                        // one when the event wasn't already fixed (prefilledEvent).
                        const base = hasWhitelabel && isSuperAdmin ? 3 : 2;
                        return `Step ${prefilledEvent ? base : base + 1}: Payment`;
                      })()}
                    </Typography>

                    <TextField
                      label="Number of tickets"
                      type="number"
                      size="small"
                      fullWidth
                      onWheel={stopWheelNudge}
                      value={eventQuantity}
                      onChange={(e) => {
                        const maxSlots = selectedEvent.availableSlots ?? 1;
                        const next = Math.max(1, Math.min(maxSlots, parseInt(e.target.value, 10) || 1));
                        setEventQuantity(next);
                      }}
                      inputProps={{ min: 1, max: selectedEvent.availableSlots ?? 1 }}
                      helperText={`${selectedEvent.availableSlots ?? 0} slots available`}
                      sx={{ mb: 2 }}
                    />

                    <Typography variant="caption" color="text.secondary" mb={0.5} display="block">Payment Method</Typography>
                    <ToggleButtonGroup
                      value={eventPayment}
                      exclusive
                      onChange={(_, val) => { if (val) setEventPayment(val); }}
                      size="small"
                      sx={{ mb: 2, flexWrap: 'wrap' }}
                    >
                      {(selectedEvent.price || 0) > 0 && (
                        <ToggleButton value="cash"><Payment sx={{ fontSize: 16, mr: 0.75 }} />Cash</ToggleButton>
                      )}
                      {(selectedEvent.price || 0) > 0 && (
                        <ToggleButton value="online"><Payment sx={{ fontSize: 16, mr: 0.75 }} />Online</ToggleButton>
                      )}
                      <ToggleButton value="free"><Check sx={{ fontSize: 16, mr: 0.75 }} />Free</ToggleButton>
                    </ToggleButtonGroup>

                    {eventPayment === 'online' && (
                      <MarkAsPaidCheckbox
                        checked={eventMarkAsPaid}
                        onChange={setEventMarkAsPaid}
                      />
                    )}

                    <Box p={2} sx={{ backgroundColor: 'grey.50', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Booking Summary</Typography>
                      {[
                        { label: 'Event', value: selectedEvent.eventName || 'N/A' },
                        { label: 'Date', value: formatDate(selectedEvent.date) },
                        { label: 'User', value: selectedUser.name || 'N/A' },
                        ...(eventPayment !== 'free' ? [
                          { label: 'Price per ticket', value: `₹${(selectedEvent.price || 0).toFixed(2)}` },
                          { label: 'Tickets', value: String(eventQuantity) },
                          { label: 'Total', value: `₹${((selectedEvent.price || 0) * eventQuantity).toFixed(2)}` },
                        ] : [{ label: 'Price', value: 'Free' }]),
                      ].map(({ label, value }) => (
                        <Box key={label} display="flex" justifyContent="space-between" mb={0.75}>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" fontWeight={600}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {eventBookingError && (
                      <Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>{eventBookingError}</Alert>
                    )}
                    {bookingError && <Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>{bookingError}</Alert>}
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      {!bookingSuccess && (
        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose} disabled={isCreatingBooking}>Cancel</Button>
          {bookingType === 'session' && selectedUser && bookingMode === 'quickCheckIn' && walkInDate && walkInTime && (
            <Button variant="contained" onClick={handleCreateAdminWalkIn}
              disabled={isCreatingBooking || (walkInPayment === 'subscription' && !walkInPack)}
              startIcon={isCreatingBooking ? <CircularProgress size={16} color="inherit" /> : undefined}>
              {isCreatingBooking ? 'Creating…' : 'Create Booking'}
            </Button>
          )}
          {bookingType === 'session' && selectedUser && bookingMode === 'session' && selectedSession && (
            <Button variant="contained" onClick={() => handleCreateRegularBooking()}
              // A seated class must carry a bike, and "not known yet" (null)
              // counts as seated — the alternative is a member on the roster
              // with no machine, which is exactly what staff bookings used to
              // produce. A class with no room is unaffected.
              disabled={
                isCreatingBooking ||
                (paymentType === 'pack' && !selectedSessionPack) ||
                (sessionHasRoom !== false && !sessionSeat)
              }
              startIcon={isCreatingBooking ? <CircularProgress size={16} color="inherit" /> : undefined}>
              {isCreatingBooking
                ? 'Creating…'
                : sessionHasRoom !== false && !sessionSeat
                  ? 'Choose a bike'
                  : 'Create Booking'}
            </Button>
          )}
          {bookingType === 'event' && selectedUser && selectedEvent && (
            <Button variant="contained" onClick={handleCreateEventBooking}
              disabled={isCreatingBooking}
              startIcon={isCreatingBooking ? <CircularProgress size={16} color="inherit" /> : undefined}>
              {isCreatingBooking ? 'Creating…' : 'Create Booking'}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>

    {/* Trial eligibility confirmation. The rules are a guard rail, not a wall:
        staff can grant a goodwill trial, but they see exactly why it was
        flagged first, and the override is audit-logged. */}
    <Dialog open={!!trialWarning} onClose={() => setTrialWarning('')} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Warning sx={{ color: 'warning.main' }} />
          <Typography fontWeight={700}>Trial already used</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          This member has already used a trial class.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setTrialWarning('')} disabled={isCreatingBooking}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={isCreatingBooking}
          startIcon={isCreatingBooking ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => { setTrialWarning(''); handleCreateRegularBooking(true); }}
        >
          Book trial anyway
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ManualBookingDialog;
